export type SelectionKey = string;

export interface SelectionChange {
  anchorKey: SelectionKey | null;
  selectedKeys: SelectionKey[];
}

export interface RangeSelectionInput {
  anchorKey: SelectionKey | null | undefined;
  orderedKeys: readonly SelectionKey[];
  selectedKeys: readonly SelectionKey[];
  targetKey: SelectionKey;
  getSelectionKey?: (orderedKey: SelectionKey) => SelectionKey | null;
}

export function toggleSelectionKey({
  selectedKeys,
  targetKey,
}: {
  selectedKeys: readonly SelectionKey[];
  targetKey: SelectionKey;
}): SelectionChange {
  return {
    anchorKey: targetKey,
    selectedKeys: selectedKeys.includes(targetKey)
      ? selectedKeys.filter(key => key !== targetKey)
      : [...selectedKeys, targetKey],
  };
}

export function replaceSelectionKey(targetKey: SelectionKey): SelectionChange {
  return {
    anchorKey: targetKey,
    selectedKeys: [targetKey],
  };
}

export function applyRangeSelection({
  anchorKey,
  orderedKeys,
  selectedKeys,
  targetKey,
  getSelectionKey = key => key,
}: RangeSelectionInput): SelectionChange {
  const fallbackToggle = () =>
    toggleSelectionKey({
      selectedKeys,
      targetKey: getSelectionKey(targetKey) ?? targetKey,
    });

  if (!anchorKey) {
    return fallbackToggle();
  }

  const anchorIndex = orderedKeys.indexOf(anchorKey);
  const targetIndex = orderedKeys.indexOf(targetKey);
  if (anchorIndex < 0 || targetIndex < 0) {
    return fallbackToggle();
  }

  const [start, end] =
    anchorIndex < targetIndex
      ? [anchorIndex, targetIndex]
      : [targetIndex, anchorIndex];
  const rangeSelectionKeys = orderedKeys
    .slice(start, end + 1)
    .map(getSelectionKey)
    .filter((key): key is SelectionKey => key !== null);

  const uniqueRangeKeys = Array.from(new Set(rangeSelectionKeys));
  if (uniqueRangeKeys.length === 0) {
    return fallbackToggle();
  }

  const selectedSet = new Set(selectedKeys);
  const removeRange = uniqueRangeKeys.every(key => selectedSet.has(key));
  if (removeRange) {
    const rangeSet = new Set(uniqueRangeKeys);
    return {
      anchorKey: null,
      selectedKeys: selectedKeys.filter(key => !rangeSet.has(key)),
    };
  }

  const next = [...selectedKeys];
  for (const key of uniqueRangeKeys) {
    if (!selectedSet.has(key)) {
      selectedSet.add(key);
      next.push(key);
    }
  }

  return {
    anchorKey: targetKey,
    selectedKeys: next,
  };
}
